import {
  CATALOG_FILTER_EXISTS,
  type EntityFilter,
} from '@backstage/plugin-catalog-react';
import type { Entity } from '@backstage/catalog-model';
import {
  TECHDOCS_ANNOTATION,
  TECHDOCS_EXTERNAL_ANNOTATION,
} from '@backstage/plugin-techdocs-common';

/**
 * Query value written to `filters[techdocs]` for the documentation view.
 * Kept as `documentation` so existing /docs deep links stay valid.
 */
export const DOCUMENTATION_QUERY_VALUE = 'documentation';

/**
 * Internal sentinel for the synthetic "Document" entry in the Kind picker.
 *
 * Deliberately not a valid entity kind (catalog kinds are alphanumeric) so a
 * real `Documentation` kind in the catalog keeps its own selectable option and
 * `filters[kind]=documentation` still resolves to that kind.
 */
export const DOCUMENTATION_KIND = '__documents__';

/**
 * Annotations that make an entity renderable by `CustomEntityTechdocsContent`.
 * An entity carrying either of them belongs in the documentation view.
 */
export const TECHDOCS_REF_ANNOTATIONS = [
  TECHDOCS_ANNOTATION,
  TECHDOCS_EXTERNAL_ANNOTATION,
];

/**
 * Placeholder filter key standing in for "has any TechDocs reference".
 *
 * The catalog list provider merges every filter into a single record and AND-s
 * the keys, so the two annotations cannot be OR-ed there. This key is emitted
 * as a marker instead and expanded into the array (OR) form of the catalog
 * filter query by {@link expandTechDocsRefFilter}, which runs just before the
 * request leaves the app. That keeps the OR server-side, and with it the
 * pagination and total count the documentation table relies on.
 *
 * It is namespaced under `metadata.annotations.` so that it stays inert if it
 * ever reaches the backend unexpanded; no entity can carry it.
 */
export const TECHDOCS_ANY_REF_FILTER_KEY =
  'metadata.annotations.__app-techdocs-any-ref__';

type CatalogFilterRecord = Record<string, string | symbol | (string | symbol)[]>;

/**
 * Replaces {@link TECHDOCS_ANY_REF_FILTER_KEY} with one filter alternative per
 * supported TechDocs annotation.
 *
 * The catalog client serializes an array of filter records into repeated
 * `filter=` parameters, which the catalog backend OR-s together, so this turns
 * "has techdocs-ref" into "has techdocs-ref or has techdocs-entity" while
 * preserving every other key of the record on each alternative. Filters
 * without the marker are returned untouched.
 */
export const expandTechDocsRefFilter = (
  filter: CatalogFilterRecord | CatalogFilterRecord[] | undefined,
): CatalogFilterRecord | CatalogFilterRecord[] | undefined => {
  if (!filter) {
    return filter;
  }

  const records = Array.isArray(filter) ? filter : [filter];
  if (!records.some(record => TECHDOCS_ANY_REF_FILTER_KEY in record)) {
    return filter;
  }

  return records.flatMap(record => {
    if (!(TECHDOCS_ANY_REF_FILTER_KEY in record)) {
      return [record];
    }
    const { [TECHDOCS_ANY_REF_FILTER_KEY]: _marker, ...rest } = record;

    // Document + real kind(s): union (kinds OR docs), not intersection. The
    // Kind picker selects chips for each group the user wants to see, so
    // Components without TechDocs and non-Component docs entities both stay.
    if ('kind' in rest) {
      const { kind, ...withoutKind } = rest;
      return [
        { kind, ...withoutKind },
        ...TECHDOCS_REF_ANNOTATIONS.map(annotation => ({
          ...withoutKind,
          [`metadata.annotations.${annotation}`]: CATALOG_FILTER_EXISTS,
        })),
      ];
    }

    return TECHDOCS_REF_ANNOTATIONS.map(annotation => ({
      ...rest,
      [`metadata.annotations.${annotation}`]: CATALOG_FILTER_EXISTS,
    }));
  });
};

/**
 * Matches every entity that publishes TechDocs, regardless of its kind.
 *
 * The unified catalog page exposes this as a synthetic "Document" entry in the
 * Kind picker, which is why it deliberately does not constrain `kind`.
 */
export class TechDocsEntityFilter implements EntityFilter {
  getCatalogFilters(): Record<string, string | symbol | (string | symbol)[]> {
    return {
      [TECHDOCS_ANY_REF_FILTER_KEY]: CATALOG_FILTER_EXISTS,
    };
  }

  /**
   * Local equivalent of the catalog filters above.
   *
   * The provider re-applies this to entities it already holds, so keeping the
   * two in sync stops results from a previously selected kind lingering in the
   * documentation view while the new request is in flight.
   */
  filterEntity(entity: Entity): boolean {
    const annotations = entity.metadata.annotations ?? {};
    return TECHDOCS_REF_ANNOTATIONS.some(annotation =>
      Boolean(annotations[annotation]),
    );
  }

  toQueryValue(): string {
    return DOCUMENTATION_QUERY_VALUE;
  }
}
